import { useCallback, useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import { AppLayout } from "@/components/layout/AppLayout";
import { PageHeader } from "@/components/layout/Common";
import { resetFavCache } from "@/components/chat/ElitePicks";
import { ToolIntro } from "@/components/chat/ToolIntro";
import { useAuth } from "@/context/AuthContext";
import { CatalogSection } from "@/components/catalog/CatalogSection";
import { api } from "@/lib/api";
import { Tabs, TabsList, TabsTrigger, TabsContent } from "@/components/ui/tabs";
import { toast } from "sonner";
import { Building2, BookOpen } from "lucide-react";

export default function Favorites() {
  const navigate = useNavigate();
  const { user } = useAuth();
  const [tab, setTab] = useState("school");
  const [catalog, setCatalog] = useState({ school: [], program: [] });
  const [favs, setFavs] = useState([]);
  const [busy, setBusy] = useState(false);

  const loadFavs = useCallback(() => api.get("/favorites", { params: { fresh: 1 } }).then((r) => setFavs(r.data)).catch(() => {}), []);

  useEffect(() => {
    loadFavs();
    api.get("/catalog/schools", { params: { fresh: 1 } }).then((r) => setCatalog((c) => ({ ...c, school: r.data }))).catch(() => {});
    api.get("/catalog/programs", { params: { fresh: 1 } }).then((r) => setCatalog((c) => ({ ...c, program: r.data }))).catch(() => {});
  }, [loadFavs]);

  const favIds = new Set(favs.map((f) => f.external_id));

  const toggle = async (item, itemType) => {
    setBusy(true);
    try {
      const { data } = await api.post("/favorites/toggle", { item_type: itemType, external_id: item.external_id });
      toast.success(data.favorited ? "Added to favorites" : "Removed from favorites");
      resetFavCache();
      await loadFavs();
    } catch (e) {
      const s = e.response?.status;
      toast.error(s === 429 ? "Please slow down a moment." : s === 409 ? "You've reached the maximum number of favorites." : "Something went wrong");
    } finally {
      setBusy(false);
    }
  };

  const askAI = (item) => navigate(`/agent?focus=${encodeURIComponent(item.external_id)}`);

  const trigger = (value, Icon, label) => (
    <TabsTrigger value={value} data-testid={`tab-${value}s`} className="rounded-full data-[state=active]:bg-orange data-[state=active]:text-white px-6 h-10 gap-2">
      <Icon className="h-4 w-4" /> {label}
    </TabsTrigger>
  );

  return (
    <AppLayout>
      <div className="max-w-6xl mx-auto px-5 py-8">
        <PageHeader title="Schools & Programs" subtitle="Browse the Elite catalog, filter what matters to you and save your favorites — synced with your Elite account." />
        <div className="mb-6 rounded-2xl overflow-hidden border border-border"><ToolIntro textKey="favorites" persona={user?.persona} /></div>
        <Tabs value={tab} onValueChange={setTab}>
          <TabsList className="mb-6 bg-white border border-border rounded-full p-1 h-auto">
            {trigger("school", Building2, "Schools")}
            {trigger("program", BookOpen, "Programs")}
          </TabsList>
          {["school", "program"].map((t) => (
            <TabsContent key={t} value={t}>
              <CatalogSection type={t} items={catalog[t]} favs={favs.filter((f) => f.item_type === t)} favIds={favIds}
                onToggle={toggle} onAskAI={askAI} busy={busy} />
            </TabsContent>
          ))}
        </Tabs>
      </div>
    </AppLayout>
  );
}
